import React from "react";
import { Event } from "@uno/protocol";
import { useTranslation } from "react-i18next";
import { AppContext } from "context";
import { useShallow } from "zustand/react/shallow";
import useStorePlayer from "store/useStorePlayer";
import useStoreToast from "store/useStoreToast";
import Avatar from "component/Ui/Avatar";
import FlagSelector from "component/Ui/FlagSelector";
import { CUSTOMIZABLE_AVATAR_PATHS } from "assets/catalog";

const USERNAME_REGEX = /^[a-zA-Z0-9._\-@=]+$/;

const CustomizeTab = () => {
    const { socket } = React.useContext(AppContext);
    const { t } = useTranslation();
    const { playerUsername, playerAvatar, playerFlag } = useStorePlayer(useShallow((state) => ({
        playerUsername: state.player?.username ?? "",
        playerAvatar: state.player?.avatar ?? "",
        playerFlag: state.player?.flag ?? "",
    })));
    const [username, setUsername] = React.useState(playerUsername || "");
    const [usernameError, setUsernameError] = React.useState<string | null>(null);
    const [selectedAvatar, setSelectedAvatar] = React.useState(playerAvatar || "");
    const scrollRef = React.useRef<HTMLDivElement>(null);

    if(!playerUsername) {
        return null;
    }

    const usernameChanged = username !== playerUsername;

    const scroll = (direction: "left" | "right") => {
        if(!scrollRef.current) {
            return;
        }

        scrollRef.current.scrollBy({ left: direction === "left" ? -140 : 140, behavior: "smooth" });
    };

    const handleSave = () => {
        const trimmed = username.trim();

        if(trimmed.length < 3 || trimmed.length > 20) {
            setUsernameError(t("customize.username_error_length"));
            return;
        }

        if(!USERNAME_REGEX.test(trimmed)) {
            setUsernameError(t("customize.username_error_characters"));
            return;
        }

        if(trimmed.startsWith("BOT-") || trimmed.endsWith("-BOT")) {
            setUsernameError(t("customize.username_error_reserved"));
            return;
        }

        socket?.emit(Event.PlayerChangeUsername, trimmed, (res: { success: boolean; error?: string }) => {
            if(res.success) {
                useStoreToast.getState().show(t("customize.username_success"), "success");
            } else if(res.error) {
                setUsernameError(t(`customize.username_error_${res.error}`));
            }
        });
    };

    return (
        <div className="flex flex-col gap-5 mt-4">
            {/* Username */}
            <div>
                <h3 className="text-white font-changa-one text-base tracking-wide">{t("customize.username_title")}</h3>
                <p className="text-white/50 text-sm mb-1">{t("customize.username_subtitle")}</p>
                <p className="text-white/40 text-xs mb-2">{t("customize.username_hint")}</p>

                <div className="flex gap-2">
                    <input
                        type="text"
                        value={username}
                        onChange={(e) => {
                            setUsername(e.target.value.toUpperCase());
                            setUsernameError(null);
                        }}
                        maxLength={20}
                        className={`flex-1 min-w-0 text-white font-changa-one text-base tracking-wide bg-white/10 border rounded-lg px-3 py-2 outline-none ${
                            usernameError ? "border-red-500/60" : "border-white/20 focus:border-white/40"
                        }`}
                    />
                    {usernameChanged && (
                        <button
                            onClick={handleSave}
                            className="shrink-0 px-4 py-2 rounded-lg font-changa-one text-white text-base tracking-wide cursor-pointer bg-white/10 border border-white/20 hover:bg-white/20 transition-colors"
                        >
                            {t("customize.save")}
                        </button>
                    )}
                </div>
                {usernameError && (
                    <p className="text-red-400 text-xs mt-1">{usernameError}</p>
                )}
            </div>

            {/* Avatar */}
            <div>
                <h3 className="text-white font-changa-one text-base tracking-wide">{t("customize.avatar_title")}</h3>
                <p className="text-white/50 text-sm mb-2">{t("customize.avatar_subtitle")}</p>

                <div className="relative">
                    <button onClick={() => scroll("left")} className="absolute -left-1 top-1/2 -translate-y-1/2 z-10 w-6 h-6 rounded-full bg-black/50 flex items-center justify-center hover:bg-black/70 transition-colors cursor-pointer">
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>

                    <div ref={scrollRef} className="flex gap-2 overflow-x-auto px-8 py-1 scrollbar-hide" style={{ scrollbarWidth: "none" }}>
                        {CUSTOMIZABLE_AVATAR_PATHS.map((avatar) => (
                            <button
                                key={avatar}
                                onClick={() => {
                                    setSelectedAvatar(avatar);
                                    socket?.emit(Event.PlayerChangeAvatar, avatar);
                                }}
                                className={`shrink-0 rounded-xl p-1.5 transition-colors cursor-pointer ${
                                    selectedAvatar === avatar
                                        ? "bg-white/15 ring-2 ring-amber-500/60"
                                        : "bg-white/5 hover:bg-white/10"
                                }`}
                            >
                                <Avatar src={avatar} className="w-14 h-14" />
                            </button>
                        ))}
                    </div>

                    <button onClick={() => scroll("right")} className="absolute -right-1 top-1/2 -translate-y-1/2 z-10 w-6 h-6 rounded-full bg-black/50 flex items-center justify-center hover:bg-black/70 transition-colors cursor-pointer">
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                </div>
            </div>

            {/* Flag */}
            <FlagSelector selectedFlag={playerFlag} onSelect={(flag) => socket?.emit(Event.PlayerChangeFlag, flag)} />

            {/* Card back — coming soon */}
            <div className="opacity-50">
                <h3 className="text-white font-changa-one text-base tracking-wide">{t("customize.card_back_title")}</h3>
                <p className="text-white/50 text-sm">{t("customize.card_back_subtitle")}</p>
            </div>

        </div>
    );
};

export default CustomizeTab;
